import { memo, useCallback, useContext, useEffect, useRef, useState } from "react"
import { Controller, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { zodResolver } from "@hookform/resolvers/zod"
import type { Editor as ToastUiEditor } from "@toast-ui/react-editor"
import { Card, CardBodyContent, CardBodyHeader, CardBodyHeaderTitle } from "@/components/ui/card"
import { Editor } from "@/components/ui/editor"
import { FormStatus } from "@/components/ui/form"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { useEditorKeyDown } from "@/hooks/useEditorKeyDown"
import { getEpisodeOfCareFragment } from "@/modules/patients/detail/episode-of-care/lib/get-episode-of-care-fragment"
import {
  EpisodeOfCareFragment$key,
  EpisodeOfCareNode,
} from "@/modules/patients/detail/episode-of-care/types/episode-of-care-fragment-type"
import { AdministrativeProgressNoteCardHeader } from "@/modules/patients/detail/progress-note/administrative-progress-note-card-header"
import { AdministrativeProgressNoteEditableCardBody } from "@/modules/patients/detail/progress-note/administrative-progress-note-editable-card-body"
import { AdministrativeProgressNoteReadOnlyCardBody } from "@/modules/patients/detail/progress-note/administrative-progress-note-read-only-card-body"
import { useIsProgressNoteEditable } from "@/modules/patients/detail/progress-note/hooks/useIsProgressNoteEditable"
import { useProgressNoteAnamnesisForm } from "@/modules/patients/detail/progress-note/hooks/useProgressNoteAnamnesisForm"
import { useProgressNoteAnamnesisFormEffect } from "@/modules/patients/detail/progress-note/hooks/useProgressNoteAnamnesisFormEffect"
import { ProgressNoteFormContext } from "@/modules/patients/detail/progress-note/hooks/useProgressNoteFormStore"
import { AnamnesisFormType, anamnesisSchema } from "@/modules/patients/detail/progress-note/schema/anamnesis-schema"
import { EpisodeOfCareProgressNoteData } from "@/modules/patients/detail/progress-note/types/progress-note-fragment-type"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { useUpdateProgressNoteAnamnesisMutation } from "@/modules/patients/services/useUpdateProgressNoteAnamnesisMutation"
import { Country } from "@/types/__generated__/union-types"

interface AdministrativeProgressNoteCardProps {
  isEditMode?: boolean
  parentEpisodeOfCare: EpisodeOfCareNode
  episodeOfCareProgressNoteData: EpisodeOfCareProgressNoteData
  country: Country
}

const AdministrativeProgressNoteCardContent = ({
  isEditMode,
  parentEpisodeOfCare,
  episodeOfCareProgressNoteData,
  country,
}: AdministrativeProgressNoteCardProps) => {
  const progressNoteFormStore = useContext(ProgressNoteFormContext)
  if (!progressNoteFormStore) throw new Error("Missing ProgressNoteFormProvider in the tree")

  const [isEditWithin24HoursAfterCloseMode, setIsEditWithin24HoursAfterCloseMode] = useState(() => {
    return progressNoteFormStore.getState().progressNoteForms.has(episodeOfCareProgressNoteData.id)
  })

  useEffect(() => {
    if (!isEditMode) setIsEditWithin24HoursAfterCloseMode(false)
  }, [isEditMode, setIsEditWithin24HoursAfterCloseMode])

  const isProgressNoteEditable = useIsProgressNoteEditable(episodeOfCareProgressNoteData)

  const { isUpdatable: isEpisodeOfCareUpdatable } = useFragment<EpisodeOfCareFragment$key>(
    getEpisodeOfCareFragment(country),
    parentEpisodeOfCare,
  )

  return (
    <Card id={episodeOfCareProgressNoteData.id} data-testid="progress-note-item">
      <AdministrativeProgressNoteCardHeader
        parentEpisodeOfCareFragmentReference={parentEpisodeOfCare}
        episodeOfCareProgressNoteData={episodeOfCareProgressNoteData}
        isEpisodeOfCareUpdatable={isEpisodeOfCareUpdatable}
        isEditWithin24HoursAfterCloseMode={isEditWithin24HoursAfterCloseMode}
        onEditWithin24HoursAfterCloseModeChange={setIsEditWithin24HoursAfterCloseMode}
      />
      {isEditMode && ((isEpisodeOfCareUpdatable && isProgressNoteEditable) || isEditWithin24HoursAfterCloseMode) ? (
        <AdministrativeProgressNoteEditableCardBody episodeOfCareProgressNoteData={episodeOfCareProgressNoteData} />
      ) : (
        <AdministrativeProgressNoteReadOnlyCardBody progressNote={episodeOfCareProgressNoteData} />
      )}
    </Card>
  )
}

const AdministrativeProgressNoteCard = memo(AdministrativeProgressNoteCardContent, areProgressNoteCardPropsEqual)

function areProgressNoteCardPropsEqual(
  oldProps: AdministrativeProgressNoteCardProps,
  newProps: AdministrativeProgressNoteCardProps,
) {
  return (
    oldProps.isEditMode === newProps.isEditMode &&
    oldProps.country === newProps.country &&
    oldProps.episodeOfCareProgressNoteData === newProps.episodeOfCareProgressNoteData
  )
}

// Currently this reuse all the data and logic from the AnamnesisSection, so there are some parts that still have the "anamnesis" in it
export const CommentSection = ({
  episodeOfCareProgressNoteData,
}: {
  episodeOfCareProgressNoteData: EpisodeOfCareProgressNoteData
}): React.JSX.Element => {
  const { t } = useTranslation()
  const country = useCountryParam()
  const commentRef = useRef<ToastUiEditor>(null)

  const { id, anamnesis } = episodeOfCareProgressNoteData
  const { defaultValues, storeFormState } = useProgressNoteAnamnesisForm(id, anamnesis)
  const updateProgressNoteAnamnesis = useUpdateProgressNoteAnamnesisMutation(country)

  const form = useForm<AnamnesisFormType>({
    resolver: zodResolver(anamnesisSchema),
    defaultValues,
    shouldFocusError: false,
  })

  useEditorKeyDown(commentRef, true)
  useProgressNoteAnamnesisFormEffect(id, episodeOfCareProgressNoteData, storeFormState, form)
  const {
    formState: { isDirty },
    reset,
    handleSubmit,
  } = form

  const handleUpdate = useCallback(() => {
    if (isDirty) {
      FaroInsightsWrapper.pushEvent("progressNote_anamnesis_save")
      handleSubmit((formData) => {
        updateProgressNoteAnamnesis({ id, anamnesis: formData.anamnesis ?? "" }, () => {
          reset(formData, { keepIsSubmitted: true })
        })
      })()
    }
  }, [handleSubmit, id, isDirty, reset, updateProgressNoteAnamnesis])

  return (
    <section>
      <CardBodyHeader data-testid="comment-title">
        <CardBodyHeaderTitle>{t("common.comment")}</CardBodyHeaderTitle>
        <div className="self-center pr-4" data-testid="comment-status">
          <FormStatus formControl={form} />
        </div>
      </CardBodyHeader>
      <CardBodyContent className="grid">
        <Controller
          control={form.control}
          name="anamnesis"
          render={({ field: { name, value, onChange }, fieldState: { error } }) => (
            <Editor
              ref={commentRef}
              minHeight="100px"
              className="[&_.toastui-editor-contents]:max-h-100 [&_.toastui-editor-contents]:overscroll-contain"
              autofocus={false}
              name={name}
              value={value}
              onChange={onChange}
              onFocus={() => FaroInsightsWrapper.pushEvent("progressNote_anamnesis_focus")}
              onBlur={() => {
                FaroInsightsWrapper.pushEvent("progressNote_anamnesis_blur")
                handleUpdate()
              }}
              error={error?.message ? t(error.message) : null}
            />
          )}
        />
      </CardBodyContent>
    </section>
  )
}

export { AdministrativeProgressNoteCard }
